<template>
  <div v-if="currentConditions && location" class="entity-weather">
    <h3 class="entity-weather__heading" data-test-id="heading">
      {{ $t('web.entitites.weather.title', { location: formattedLocation }) }}
    </h3>
    <a
      class="entity-weather__current"
      :href="linkWithPartnerCode"
      data-test-id="current-weather"
      @click="onLinkClick('body')"
    >
      <EAccuweatherIcon
        :code="currentConditions.WeatherIcon"
        data-test-id="icon"
        aria-hidden="true"
      />
      <div class="entity-weather__data">
        <div class="entity-weather__temperature" data-test-id="temperature">
          {{ temperature.Value }}°{{ temperature.Unit }}
        </div>
        <div class="entity-weather__details" data-test-id="text">
          {{ currentConditions.WeatherText }}
        </div>
      </div>
      <EIcon
        icon="chevron-right"
        size="s"
        class="entity-weather__current-chevron"
        aria-hidden="true"
      />
    </a>
    <ELink
      :href="linkWithPartnerCode"
      :target="linkTarget"
      color="result"
      data-test-id="link"
      @click="onLinkClick('more')"
    >
      {{ $t('web.entitites.weather.more') }}
    </ELink>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import EcosiaUser from '@ecosia/ecosia-user';

import { MAIN as MAIN_COOKIE } from '~~/common/js/constants/cookies';
import { localisePlainNumber } from '~~/common/js/universal/intl';
import ELink from '~~/common/vue/components/link';
import EIcon from '~~/common/vue/components/icon';
import { trackClick, trackImpression, PAGE_SERP, TYPE_ENTITY } from '~~/search/common/js/client/analytics/rich-content';

import { getTemperatureUnit } from '~/components/weather/temperature';
import EAccuweatherIcon from '~/components/weather/icons/accuweather';

import { getLocation, getCurrentConditions } from './api';
import { PARTNER_CODE } from './constants';

export const TRACK_ID = 'weather_extension';

export default {
  name: 'EEntityWeather',
  components: {
    EAccuweatherIcon,
    EIcon,
    ELink,
  },
  props: {
    locationName: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    currentConditions: null,
    location: null,
    metric: false,
  }),
  computed: {
    ...mapState('global', ['locale']),
    ...mapGetters('user', ['linkTarget']),
    unitKey() {
      return this.metric ? 'Metric' : 'Imperial';
    },
    temperature() {
      const { Value, Unit } = this.currentConditions?.Temperature[this.unitKey];
      return { Value: localisePlainNumber(this.locale, Value), Unit };
    },
    // Accuweather sometimes can't recognise place names including certain characters,
    // e.g. "Washington, D.C." returns 404 but "Washington D.C." works.
    sanitisedLocationName() {
      return this.locationName.replace(/,/g, '');
    },
    formattedLocation() {
      return [this.location.LocalizedName, this.location.Country.LocalizedName].join(', ');
    },
    sources() {
      return [
        { title: 'AccuWeather', url: this.linkWithPartnerCode },
      ];
    },
    linkWithPartnerCode() {
      if (!this.currentConditions) {
        return '';
      }

      const url = new URL(this.currentConditions.Link);
      url.searchParams.set('partner', PARTNER_CODE);

      return url.toString();
    },
  },
  async mounted() {
    // TODO: refactor this and consolidate EcosiaUser usage across the codebase
    const cookieString = this.$cookies.get(MAIN_COOKIE);
    this.user = new EcosiaUser({ cookieString });

    try {
      this.location = await this.getLocation();
      if (this.location) {
        const units = getTemperatureUnit(
          this.user.get('temperatureUnit'),
          this.user.get('bingMarketCode'),
        );

        this.metric = units === 'metric';
        this.currentConditions = await this.getCurrentConditions(this.location.Key);

        if (this.currentConditions) {
          this.$emit('sources', this.sources);
          trackImpression({
            name: TRACK_ID,
            page: PAGE_SERP,
            type: TYPE_ENTITY,
            once: true,
          });
        }
      }
    } catch (err) {
      this.$sentry.captureException(err);
    }
  },
  methods: {
    async getLocation() {
      return getLocation({
        name: this.sanitisedLocationName,
        locale: this.locale,
      });
    },
    async getCurrentConditions(location) {
      return getCurrentConditions({
        location,
        locale: this.locale,
        metric: this.metric,
      });
    },
    onLinkClick(link) {
      const track = trackClick({
        name: TRACK_ID,
        type: TYPE_ENTITY,
        target: link,
      });

      track();
    },
  },
};
</script>

<style lang="scss" scoped>
.entity-weather {
  @include divider(top);

  margin-top: $space-1l;
  padding: $space-m 0 0;
}

.entity-weather__heading {
  color: var(--color-text-secondary);
  font-weight: $font-weight-m;
  font-size: inherit;
  margin: 0;
}

.entity-weather__current {
  align-items: center;
  border-radius: $border-radius-l;
  display: flex;
  margin: $space-1s 0;
  padding: $space-1s;
  transition: background-color $easing $timing-2s;

  &:hover,
  &:active {
    background-color: var(--color-button-background-transparent-hover);
  }

  &:focus {
    @extend %keyboard-nav-focus;
  }
}

.entity-weather__temperature {
  font-size: $font-l;
  font-weight: $font-weight-1l;
}

.entity-weather__data {
  margin-left: $space-1s;
}

.entity-weather__current-chevron {
  margin-left: auto;
  margin-right: 0;
}
</style>
